import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useMemo,
} from 'react';
import { useIntegrationApp } from '@membranehq/react';
import {
  ContactRecord,
  DataSourceKey,
  listAction,
  ListActionResponse,
  ContactRecordField,
  MondayListParams,
} from 'src/services/integration-app.service';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { expandExpressions } from 'src/utils/expressionUtils';
import {
  formatPhoneNumberWithError,
  processPhoneNumber,
} from 'src/utils/number';
import {
  WorkflowDataSourceRecordDto,
  WorkflowDataSourceProvider,
  BatchCreateWorkflowExecutionDto,
  BatchCreateWorkflowExecutionResponseDto,
  ExecutionDeduplicationAction,
} from 'src/interfaces/workflows.interface';
import { EVENT_EXPRESSION_ROOT } from '../components/wizard/WizardContext';
import { Option } from 'src/components/SelectBox';
import {
  NotificationMethods,
  useNotification,
} from 'src/contexts/NotificationContext';
import { ContactFilterType, FilterOperator } from '../ContactFilter';
import { useCrmSync, MondayConfig } from './CrmSyncContext';
import { useCampaignContext } from './CampaignContext';
import { useFieldMapping } from './FieldMappingContext';
import { useAnalytics } from 'src/hooks/useAnalytics';
import { ImportError, SyncSummary } from '../types/sync.types';

export enum DuplicateHandlingOption {
  SKIP = 'skip',
  KEEP_BOTH = 'keep_both',
  UPDATE = 'update',
}

interface ContactsContextValue {
  // State
  previewContacts: ContactRecord[];
  filters: ContactFilterType[];
  currentPage: number;
  size: number;
  filteredContacts: ContactRecord[];
  paginatedContacts: ContactRecord[];
  isLoading: boolean;
  isImporting: boolean;
  numContactsSynced: number;
  syncSummary: SyncSummary | null;
  duplicateHandlingOption: DuplicateHandlingOption;

  // Setters
  setPreviewContacts: (contacts: ContactRecord[]) => void;
  setFilters: (filters: ContactFilterType[]) => void;
  setCurrentPage: (page: number) => void;
  setSize: (size: number) => void;
  setDuplicateHandlingOption: (option: DuplicateHandlingOption) => void;

  // Actions
  fetchContactsFromCrm: (
    integratedCrm: Option | null,
    segmentId?: string,
    mondayConfig?: MondayConfig,
    maxContacts?: number,
  ) => Promise<ContactRecord[]>;
  handleImport: () => Promise<SyncSummary>;
  handlePreviewUpload: (
    integratedCrm: Option | null,
    segmentId: string | undefined,
    mondayConfig: MondayConfig,
    handleStepNavigation: (direction: 'back' | 'continue') => void,
    notification: NotificationMethods,
  ) => Promise<void>;
  resetImportState: () => void;
  applyFilters: (contactsToFilter: ContactRecord[]) => ContactRecord[];
  getFilterSummary: () => string;
  handlePageChange: (page: number, perPage: number) => void;
  handleSizeChange: (perPage: number) => void;
  handleSort: () => void;
}

const ContactsContext = createContext<ContactsContextValue | undefined>(
  undefined,
);

export const useContacts = () => {
  const context = useContext(ContactsContext);
  if (context === undefined) {
    throw new Error('useContacts must be used within a ContactsProvider');
  }
  return context;
};

interface ContactsProviderProps {
  children: ReactNode;
}

export const ContactsProvider: React.FC<ContactsProviderProps> = ({
  children,
}) => {
  const integrationApp = useIntegrationApp();
  const { post } = useAuthedRequest();
  const { integratedCrm, segmentId, mondayConfig, setImportErrors } = useCrmSync();
  const { campaign } = useCampaignContext();
  const { fieldMapping } = useFieldMapping();
  const notification = useNotification();
  const { track } = useAnalytics();

  // State
  const [previewContacts, setPreviewContacts] = useState<ContactRecord[]>([]);
  const [filters, setFilters] = useState<ContactFilterType[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [size, setSize] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [numContactsSynced, setNumContactsSynced] = useState(0);
  const [syncSummary, setSyncSummary] = useState<SyncSummary | null>(null);
  const [duplicateHandlingOption, setDuplicateHandlingOption] =
    useState<DuplicateHandlingOption>(DuplicateHandlingOption.SKIP);

  // Filter and pagination logic
  const applyFilters = (contactsToFilter: ContactRecord[]): ContactRecord[] => {
    if (filters.length === 0) return contactsToFilter;

    return contactsToFilter.filter((contact) => {
      return filters.every((filter) => {
        const rawFieldValue =
          contact.fields[filter.field as keyof typeof contact.fields];

        const fieldValue = rawFieldValue != null ? String(rawFieldValue) : '';
        const filterValue = filter.value.toLowerCase();
        const contactValue = fieldValue.toLowerCase();

        switch (filter.operator) {
          case FilterOperator.CONTAINS:
            return contactValue.includes(filterValue);
          case FilterOperator.EQUALS:
            return contactValue === filterValue;
          case FilterOperator.STARTS_WITH:
            return contactValue.startsWith(filterValue);
          case FilterOperator.ENDS_WITH:
            return contactValue.endsWith(filterValue);
          case FilterOperator.NOT_EMPTY:
            return fieldValue.trim() !== '';
          case FilterOperator.EMPTY:
            return fieldValue.trim() === '';
          default:
            return true;
        }
      });
    });
  };

  const getFilterSummary = () => {
    if (filters.length === 0) return 'All contacts will be imported';
    return `Contacts will be filtered by ${filters.length} condition${filters.length === 1 ? '' : 's'}`;
  };

  // Table handlers
  const handlePageChange = (page: number, perPage: number) => {
    setCurrentPage(page);
    setSize(perPage);
  };

  const handleSizeChange = (perPage: number) => {
    setSize(perPage);
    setCurrentPage(1);
  };

  const handleSort = () => {
    // Sorting disabled for preview table
  };

  // Computed values
  const filteredContacts = useMemo(
    () => applyFilters(previewContacts),
    [previewContacts, filters],
  );

  const paginatedContacts = useMemo(() => {
    const startIndex = (currentPage - 1) * size;
    const endIndex = startIndex + size;
    return filteredContacts.slice(startIndex, endIndex);
  }, [filteredContacts, currentPage, size]);

  // Data fetching functions
  const fetchContactsFromCrm = async (
    integratedCrm: Option | null,
    segmentId?: string,
    mondayConfig?: MondayConfig,
    maxContacts?: number,
  ): Promise<ContactRecord[]> => {
    const mondayParams: MondayListParams | undefined =
      mondayConfig?.boardId && mondayConfig?.groupId
        ? {
            board_id: mondayConfig.boardId,
            group_id: mondayConfig.groupId,
          }
        : undefined;

    const response = await listAction<ContactRecord>({
      client: integrationApp,
      integrationKey: integratedCrm?.value || null,
      dataSourceKey: DataSourceKey.CONTACTS,
      segmentId,
      mondayParams,
      maxRecords: maxContacts,
    });

    return response.records;
  };

  const handleImport = async (): Promise<SyncSummary> => {
    if (!integratedCrm || !campaign) {
      const emptySummary: SyncSummary = {
        newContacts: 0,
        updatedContacts: 0,
        skippedContacts: 0,
        totalProcessed: 0,
        errors: [],
        hasErrors: false,
      };
      return emptySummary;
    }

    setIsImporting(true);
    setNumContactsSynced(0); // Reset counter at start of import

    // Reset errors at start of import
    setImportErrors([]);

    try {
      const summary = await processContactsStream();
      setSyncSummary(summary);
      return summary;
    } catch (error) {
      console.error('Failed to import contacts:', error);
      track.errorExperienced({
        error: 'crm_contact_import_failed',
        message:
          error instanceof Error ? error.message : 'Unknown error occurred',
      });
      notification.error('Failed to import contacts');
      const errorSummary: SyncSummary = {
        newContacts: 0,
        updatedContacts: 0,
        skippedContacts: 0,
        totalProcessed: 0,
        errors: [
          {
            recordDescription: 'Import failed',
            message:
              error instanceof Error ? error.message : 'Unknown error occurred',
          },
        ],
        hasErrors: true,
      };
      setSyncSummary(errorSummary);
      return errorSummary;
    } finally {
      setIsImporting(false);
    }
  };

  const processContactsStream = async (): Promise<SyncSummary> => {
    if (!integratedCrm || !campaign) {
      return {
        newContacts: 0,
        updatedContacts: 0,
        skippedContacts: 0,
        totalProcessed: 0,
        errors: [],
        hasErrors: false,
      };
    }

    let totalNewContacts = 0;
    let totalUpdatedContacts = 0;
    let totalSkippedContacts = 0;
    const allErrors: ImportError[] = [];
    let cursor: string | undefined;

    do {
      try {
        const mondayParams: MondayListParams | undefined =
          mondayConfig?.boardId && mondayConfig?.groupId
            ? {
                board_id: mondayConfig.boardId,
                group_id: mondayConfig.groupId,
              }
            : undefined;

        const listActionParams: {
          client: typeof integrationApp;
          integrationKey: string;
          dataSourceKey: string;
          cursor?: string;
          segmentId?: string;
          mondayParams?: MondayListParams;
        } = {
          client: integrationApp,
          integrationKey: integratedCrm.value,
          dataSourceKey: DataSourceKey.CONTACTS,
          cursor,
          segmentId,
          mondayParams,
        };

        const page: ListActionResponse<ContactRecord> = await listAction({
          ...listActionParams,
        });

        const filteredContacts = applyFilters(page.records);
        const totalContactsInPage = page.records.length;
        const skippedByFilters = totalContactsInPage - filteredContacts.length;
        totalSkippedContacts += skippedByFilters;

        if (filteredContacts.length > 0) {
          try {
            const batchResult = await addContacts(filteredContacts);
            totalNewContacts += batchResult.successfulContacts;
            totalUpdatedContacts += batchResult.updatedContacts;
            totalSkippedContacts += batchResult.skippedContacts;
            allErrors.push(...batchResult.errors);

            setNumContactsSynced(totalNewContacts);
          } catch (error) {
            console.error('Failed to add contacts for this batch:', error);
            track.errorExperienced({
              error: 'crm_contact_batch_failed',
              message: error instanceof Error ? error.message : 'Unknown error',
            });
            const batchError: ImportError = {
              recordDescription: 'Batch processing',
              message: `Failed to process a batch of ${filteredContacts.length} contacts: ${error instanceof Error ? error.message : 'Unknown error'}`,
            };
            allErrors.push(batchError);
            totalSkippedContacts += filteredContacts.length;
          }
        }

        cursor = page.cursor;
      } catch (error) {
        console.error('Failed to fetch contacts page:', error);
        track.errorExperienced({
          error: 'crm_contact_fetch_failed',
          message: error instanceof Error ? error.message : 'Unknown error',
        });
        const fetchError: ImportError = {
          recordDescription: 'Data retrieval',
          message: `Failed to fetch contacts from CRM: ${error instanceof Error ? error.message : 'Unknown error'}`,
        };
        allErrors.push(fetchError);
        cursor = undefined;
      }
    } while (cursor);

    // Update import errors in context
    setImportErrors(allErrors);

    const summary: SyncSummary = {
      newContacts: totalNewContacts,
      updatedContacts: totalUpdatedContacts,
      skippedContacts: totalSkippedContacts,
      totalProcessed:
        totalNewContacts + totalUpdatedContacts + totalSkippedContacts,
      errors: allErrors,
      hasErrors: allErrors.length > 0,
    };

    return summary;
  };

  const addContacts = async (
    contactRecords: ContactRecord[],
  ): Promise<{
    successfulContacts: number;
    updatedContacts: number;
    skippedContacts: number;
    errors: ImportError[];
  }> => {
    if (!integratedCrm || !campaign) {
      return {
        successfulContacts: 0,
        updatedContacts: 0,
        skippedContacts: 0,
        errors: [],
      };
    }

    const errors: ImportError[] = [];
    const validContacts: {
      contactRecord: ContactRecord;
      fields: Record<string, unknown>;
    }[] = [];

    for (let index = 0; index < contactRecords.length; index++) {
      const contactRecord = contactRecords[index];

      // Apply field mapping to get the mapped values
      const mappedParams = expandExpressions(
        fieldMapping as Record<string, unknown>,
        {
          [EVENT_EXPRESSION_ROOT]: contactRecord,
        },
      );

      // Check if phone number is present after field mapping
      const phoneValue = mappedParams.to_number;
      if (!phoneValue) {
        errors.push({
          recordDescription: `Contact ${contactRecord.id} | ${phoneValue}`,
          message: 'Missing or invalid phone number',
        });
        continue;
      }

      const { number, error } = formatPhoneNumberWithError(String(phoneValue));
      if (error) {
        errors.push({
          recordDescription: `Contact ${contactRecord.id}`,
          message: `Invalid phone number: ${phoneValue}`,
        });
        continue;
      }

      const contextParams: Record<string, unknown> = {};
      const directParams: Record<string, unknown> = {
        to_number: number,
        [ContactRecordField.ID]: contactRecord.id,
      };

      Object.entries(mappedParams).forEach(([key, value]) => {
        if (key.startsWith('context.')) {
          const contextKey = key.replace('context.', '');
          contextParams[contextKey] = value;
        } else if (key !== 'to_number') {
          directParams[key] = value;
        }
      });

      validContacts.push({
        contactRecord,
        fields: {
          ...directParams,
          ...(Object.keys(contextParams).length > 0 && {
            context: contextParams,
          }),
        },
      });
    }

    const skippedContacts = contactRecords.length - validContacts.length;

    if (validContacts.length === 0) {
      return {
        successfulContacts: 0,
        updatedContacts: 0,
        skippedContacts,
        errors,
      };
    }

    const batchSize = 50;
    let processedCount = 0;
    let updatedCount = 0;
    let duplicatesSkippedCount = 0;

    for (let i = 0; i < validContacts.length; i += batchSize) {
      const batch = validContacts.slice(i, i + batchSize);

      // Prepare batch payload for single API call
      const batchPayload: BatchCreateWorkflowExecutionDto = {
        workflowReferenceId: campaign.referenceId,
        executions: batch.map(({ contactRecord, fields }) => ({
          params: fields,
          sourceRecord: {
            source_provider: WorkflowDataSourceProvider.INTEGRATION_APP,
            source_id: campaign.crmCampaignConfig?.provider || '',
            id: contactRecord.id,
            data_source_key: DataSourceKey.CONTACTS,
          } as WorkflowDataSourceRecordDto,
        })),
      };

      // Apply deduplication if user chooses to discard or update
      if (duplicateHandlingOption !== DuplicateHandlingOption.KEEP_BOTH) {
        batchPayload.deduplicationConfig = {
          uniqueParamKeys: ['to_number'],
          onDuplicate:
            duplicateHandlingOption === DuplicateHandlingOption.UPDATE
              ? ExecutionDeduplicationAction.UPDATE
              : ExecutionDeduplicationAction.SKIP,
        };
      }

      const batchResponse: BatchCreateWorkflowExecutionResponseDto = await post(
        '/workflow_executions/batch',
        batchPayload,
      );

      let batchSuccessCount = 0;
      for (let index = 0; index < batchResponse.executions.length; index++) {
        const execution = batchResponse.executions[index];
        const { contactRecord } = batch[index];

        if (execution.error) {
          // ERROR case - always treat as error, never as duplicate
          errors.push({
            recordDescription: `Contact ${contactRecord.id}`,
            message: execution.error,
          });
          continue;
        }

        if (
          execution.duplicateResolutionAction ===
          ExecutionDeduplicationAction.UPDATE
        ) {
          // UPDATE case - duplicate was updated
          updatedCount++;
        } else if (
          execution.duplicateResolutionAction ===
          ExecutionDeduplicationAction.SKIP
        ) {
          // SKIP case - duplicate was skipped
          duplicatesSkippedCount++;
          const phone = String(
            (batch[index].fields as Record<string, unknown>)?.to_number || '',
          );
          errors.push({
            recordDescription: `Contact ${contactRecord.id}`,
            message: `Duplicate contact skipped - contact already exists in campaign with phone number ${processPhoneNumber(
              phone,
            )}`,
          });
        } else if (execution.id) {
          // INSERT case - new execution created
          batchSuccessCount++;
        }
      }

      processedCount += batchSuccessCount;
    }

    return {
      successfulContacts: processedCount,
      updatedContacts: updatedCount,
      skippedContacts: skippedContacts + duplicatesSkippedCount,
      errors,
    };
  };

  const handlePreviewUpload = async (
    integratedCrm: Option | null,
    segmentId: string | undefined,
    mondayConfig: MondayConfig,
    handleStepNavigation: (direction: 'back' | 'continue') => void,
    notification: NotificationMethods,
  ) => {
    if (!integratedCrm) return;

    setIsLoading(true);
    try {
      // Fetch contacts for preview
      const contacts = await fetchContactsFromCrm(
        integratedCrm,
        segmentId,
        mondayConfig,
        200,
      ); // Increased limit for preview to allow for filtering
      setPreviewContacts(contacts);
      // Use context navigation to go to preview step
      handleStepNavigation('continue');
    } catch (error) {
      console.error('Failed to fetch contacts for preview:', error);
      track.errorExperienced({
        error: 'crm_contact_preview_failed',
        message: error instanceof Error ? error.message : 'Unknown error',
      });
      notification.error('Failed to load contacts for preview');
    } finally {
      setIsLoading(false);
    }
  };

  const resetImportState = () => {
    setNumContactsSynced(0);
    setImportErrors([]);
    setSyncSummary(null);
  };

  const value: ContactsContextValue = {
    // State
    previewContacts,
    filters,
    currentPage,
    size,
    filteredContacts,
    paginatedContacts,
    isLoading,
    isImporting,
    numContactsSynced,
    syncSummary,
    duplicateHandlingOption,

    // Setters
    setPreviewContacts,
    setFilters,
    setCurrentPage,
    setSize,
    setDuplicateHandlingOption,

    // Actions
    fetchContactsFromCrm,
    handleImport,
    handlePreviewUpload,
    resetImportState,
    applyFilters,
    getFilterSummary,
    handlePageChange,
    handleSizeChange,
    handleSort,
  };

  return (
    <ContactsContext.Provider value={value}>
      {children}
    </ContactsContext.Provider>
  );
};
